import React from 'react';
import { Textarea } from '@roger/ui-components';
import { X12_997_Response } from '../types/x12';

interface X12_997_Response_DisplayProps {
  waystarResponse: {
    x12_997_message?: string;
    parsed_997_response?: X12_997_Response;
  };
}

const X12_997_Response_Display: React.FC<X12_997_Response_DisplayProps> = ({
  waystarResponse,
}) => {
  return (
    <>
      {waystarResponse.x12_997_message && (
        <div>
          <label className='block text-sm font-medium mb-2'>
            Raw X12 997 Message
          </label>
          <Textarea
            value={waystarResponse.x12_997_message}
            readOnly
            className='font-mono text-xs h-32'
            placeholder='X12 997 response will appear here...'
          />
        </div>
      )}

      {waystarResponse.parsed_997_response && (
        <div>
          <label className='block text-sm font-medium mb-2'>
            Parsed X12 997 Response
          </label>
          <div className='space-y-2 p-4 bg-gray-50 rounded-lg'>
            {waystarResponse.parsed_997_response.interchange_control_number && (
              <div>
                <span className='text-xs font-medium'>
                  Interchange Control Number:{' '}
                  {
                    waystarResponse.parsed_997_response
                      .interchange_control_number
                  }
                </span>
              </div>
            )}

            {waystarResponse.parsed_997_response
              .functional_group_acknowledgments?.length > 0 && (
              <div>
                <span className='text-xs font-medium'>
                  Functional Group Acknowledgments:
                </span>
                <div className='mt-2 space-y-3'>
                  {waystarResponse.parsed_997_response.functional_group_acknowledgments.map(
                    (fg_ack, fg_index) => (
                      <div
                        key={fg_index}
                        className='p-3 bg-white rounded border'
                      >
                        <div className='grid grid-cols-2 gap-2 text-sm mb-2'>
                          <div>
                            <span className='text-xs font-medium'>
                              Functional Identifier:{' '}
                              {fg_ack.functional_identifier_code}
                            </span>
                          </div>
                          <div>
                            <span className='text-xs font-medium'>
                              Group Control Number:{' '}
                              {fg_ack.group_control_number}
                            </span>
                          </div>
                          <div>
                            <span className='text-xs font-medium'>
                              Acknowledgment Code:
                            </span>{' '}
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                fg_ack.group_acknowledgment_code === 'A'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {fg_ack.group_acknowledgment_code}&nbsp;
                              {fg_ack.group_acknowledgment_code === 'A'
                                ? '(Accepted)'
                                : '(Rejected)'}
                            </span>
                          </div>
                        </div>

                        {fg_ack.transaction_set_acknowledgments.length > 0 && (
                          <div className='mt-3'>
                            <span className='text-xs font-medium'>
                              Transaction Set Acknowledgments:
                            </span>
                            <div className='mt-2 space-y-2'>
                              {fg_ack.transaction_set_acknowledgments.map(
                                (ts_ack, ts_index) => (
                                  <div
                                    key={ts_index}
                                    className='p-2 bg-gray-50 rounded border-l-4 border-blue-300'
                                  >
                                    <div className='grid grid-cols-2 gap-2 text-xs mb-2'>
                                      <div>
                                        <span className='text-xs font-medium'>
                                          Transaction Set ID:
                                        </span>{' '}
                                        {ts_ack.transaction_set_identifier_code}
                                      </div>
                                      <div>
                                        <span className='text-xs font-medium'>
                                          Control Number:
                                        </span>{' '}
                                        {ts_ack.transaction_set_control_number}
                                      </div>
                                      <div>
                                        <span className='text-xs font-medium'>
                                          Acknowledgment Code:
                                        </span>{' '}
                                        <span
                                          className={`px-2 py-1 rounded text-xs ${
                                            ts_ack.transaction_set_acknowledgment_code ===
                                            'A'
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-red-100 text-red-800'
                                          }`}
                                        >
                                          {
                                            ts_ack.transaction_set_acknowledgment_code
                                          }
                                          &nbsp;
                                          {ts_ack.transaction_set_acknowledgment_code ===
                                          'A'
                                            ? '(Accepted)'
                                            : '(Rejected)'}
                                        </span>
                                      </div>
                                      {ts_ack.transaction_set_syntax_error_code && (
                                        <div>
                                          <span className='text-xs font-medium'>
                                            Syntax Error Code:
                                          </span>{' '}
                                          <span className='text-red-600'>
                                            {
                                              ts_ack.transaction_set_syntax_error_code
                                            }
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {ts_ack.data_segment_notes.length > 0 && (
                                      <div className='mt-2'>
                                        <span className='text-xs font-medium'>
                                          Data Segment Notes:
                                        </span>
                                        <div className='mt-1 space-y-1'>
                                          {ts_ack.data_segment_notes.map(
                                            (ds_note, ds_index) => (
                                              <div
                                                key={ds_index}
                                                className='p-1 bg-red-50 rounded text-xs'
                                              >
                                                <div className='grid grid-cols-2 gap-1'>
                                                  <div>
                                                    <span className='text-xs font-medium'>
                                                      Segment ID:
                                                    </span>{' '}
                                                    {ds_note.segment_id_code}
                                                  </div>
                                                  <div>
                                                    <span className='text-xs font-medium'>
                                                      Position:
                                                    </span>{' '}
                                                    {
                                                      ds_note.segment_position_in_transaction_set
                                                    }
                                                  </div>
                                                  <div>
                                                    <span className='text-xs font-medium'>
                                                      Note Code:
                                                    </span>{' '}
                                                    <span className='text-red-600'>
                                                      {
                                                        ds_note.data_segment_note_code
                                                      }
                                                    </span>
                                                  </div>
                                                </div>
                                                {ds_note.copy_of_bad_segment && (
                                                  <div className='mt-1'>
                                                    <span className='text-xs font-medium'>
                                                      Bad Segment:
                                                    </span>{' '}
                                                    <span className='font-mono text-red-600'>
                                                      {
                                                        ds_note.copy_of_bad_segment
                                                      }
                                                    </span>
                                                  </div>
                                                )}

                                                {ds_note.data_element_notes &&
                                                  ds_note.data_element_notes
                                                    .length > 0 && (
                                                    <div className='mt-2'>
                                                      <span className='text-xs font-medium'>
                                                        Data Element Notes:
                                                      </span>
                                                      <div className='mt-1 space-y-1'>
                                                        {ds_note.data_element_notes.map(
                                                          (
                                                            de_note,
                                                            de_index
                                                          ) => (
                                                            <div
                                                              key={de_index}
                                                              className='p-1 bg-orange-50 rounded text-xs'
                                                            >
                                                              <div className='grid grid-cols-2 gap-1'>
                                                                <div>
                                                                  <span className='text-xs font-medium'>
                                                                    Element
                                                                    Position:
                                                                  </span>{' '}
                                                                  {
                                                                    de_note.element_position_in_segment
                                                                  }
                                                                </div>
                                                                <div>
                                                                  <span className='text-xs font-medium'>
                                                                    Reference:
                                                                  </span>{' '}
                                                                  {
                                                                    de_note.data_element_reference_number
                                                                  }
                                                                </div>
                                                                <div>
                                                                  <span className='text-xs font-medium'>
                                                                    Note Code:
                                                                  </span>{' '}
                                                                  <span className='text-orange-600'>
                                                                    {
                                                                      de_note.data_element_note_code
                                                                    }
                                                                  </span>
                                                                </div>
                                                              </div>
                                                              {de_note.copy_of_bad_data_element && (
                                                                <div className='mt-1'>
                                                                  <span className='text-xs font-medium'>
                                                                    Bad Element:
                                                                  </span>{' '}
                                                                  <span className='font-mono text-orange-600'>
                                                                    {
                                                                      de_note.copy_of_bad_data_element
                                                                    }
                                                                  </span>
                                                                </div>
                                                              )}
                                                            </div>
                                                          )
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {waystarResponse.parsed_997_response.errors?.length > 0 && (
              <div>
                <span className='text-sm font-medium text-red-600'>
                  Parsing Errors:
                </span>
                <ul className='mt-2 list-disc list-inside text-red-600'>
                  {waystarResponse.parsed_997_response.errors.map(
                    (error, index) => (
                      <li key={index}>{error}</li>
                    )
                  )}
                </ul>
              </div>
            )}

            {waystarResponse.parsed_997_response.raw_segments?.length > 0 && (
              <div className='mt-6'>
                <div className='overflow-x-auto mt-2'>
                  <table className='min-w-full text-xs border'>
                    <thead>
                      <tr className='bg-gray-100'>
                        <th className='px-2 py-1 border'>Segment ID</th>
                        <th className='px-2 py-1 border'>Elements</th>
                        <th className='px-2 py-1 border'>Raw Text</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waystarResponse.parsed_997_response.raw_segments.map(
                        (
                          seg: {
                            segment_id: string;
                            elements: string[];
                            raw_text: string;
                          },
                          idx: number
                        ) => (
                          <tr key={idx} className='border-t'>
                            <td className='px-2 py-1 border font-mono'>
                              {seg.segment_id}
                            </td>
                            <td className='px-2 py-1 border font-mono'>
                              {Array.isArray(seg.elements)
                                ? seg.elements.join(', ')
                                : String(seg.elements)}
                            </td>
                            <td className='px-2 py-1 border font-mono'>
                              {seg.raw_text}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default X12_997_Response_Display;
